import { isEmpty, isNumber } from "./validationUtil";
import { startOfLocalDay, getLatestActivityDate, getEarliestActivityDate } from "./dateUtil";
import * as AppConstants from "AppConstants";

export const ACTIVITY_FIELD_MESSAGES = {
  hoursSpent: {
    required: "Required. Greater than 0, up to 12.",
    notNumber: "Enter a valid number.",
    tooLow: "Hours Spent must be greater than 0.",
    tooHigh: "Hours Spent cannot exceed 12.",
    tooManyDecimals: "Enter at most one decimal place (e.g. 7.5).",
  },
  ownerId: { required: "Select a member." },
  managerId: { required: "Manager profile unresolved. Try reloading." },
  activityTypeId: { required: "Select an activity type." },
  startDate: {
    required: "Pick a start date.",
    tooFarAhead: `Start date cannot be more than ${AppConstants.ACTIVITY_FUTURE_DAYS_ALLOWED} days ahead.`,
    // @since NEXT_VERSION
    tooFarBack: `Start date cannot be more than ${AppConstants.ACTIVITY_PAST_MONTHS_ALLOWED} months old.`,
  },
  endDate: {
    required: "Pick an end date.",
    mismatch: "End date must match start date.",
  },
  status: { required: "Pick a valid status." },
  description: { tooLong: "Description must be under 255 characters." },
};

const HOURS_MAX = AppConstants.MAXIMUM_ACTIVITY_HOURS_PER_DAY; // 12

const validateHoursField = (hrs) => {
  if (hrs === undefined || hrs === null || (typeof hrs === "number" && Number.isNaN(hrs))) {
    return ACTIVITY_FIELD_MESSAGES.hoursSpent.required;
  }
  if (!isNumber(hrs)) return ACTIVITY_FIELD_MESSAGES.hoursSpent.notNumber;
  if (hrs <= 0) return ACTIVITY_FIELD_MESSAGES.hoursSpent.tooLow;
  if (hrs > HOURS_MAX) return ACTIVITY_FIELD_MESSAGES.hoursSpent.tooHigh;
  if (Math.abs(Math.round(hrs * 10) - hrs * 10) > 1e-9) {
    return ACTIVITY_FIELD_MESSAGES.hoursSpent.tooManyDecimals;
  }
  return null;
};

export const isValidHours = (hrs) => validateHoursField(hrs) === null;

const validateOwnerField = (ownerId) => {
  if (isEmpty(ownerId)) return ACTIVITY_FIELD_MESSAGES.ownerId.required;
  if (typeof ownerId === "number" && Number.isNaN(ownerId)) return ACTIVITY_FIELD_MESSAGES.ownerId.required;
  return null;
};

const validateActivityTypeField = (activityTypeId) => {
  if (isEmpty(activityTypeId)) return ACTIVITY_FIELD_MESSAGES.activityTypeId.required;
  if (typeof activityTypeId === "number" && Number.isNaN(activityTypeId)) return ACTIVITY_FIELD_MESSAGES.activityTypeId.required;
  return null;
};

// How far ahead a date sits is a question about calendar days, not instants. Comparing against
// Date.now() answered the instant question: a date-only string is UTC midnight, so for a UTC+X
// user today's own date sat X hours ahead of now and read as future — blocking activity logging
// between local midnight and UTC midnight (00:00–05:30 daily in IST).
const validateStartDateField = (startDate, earliestDate) => {
  if (isEmpty(startDate)) return ACTIVITY_FIELD_MESSAGES.startDate.required;
  if (startOfLocalDay(startDate) > getLatestActivityDate()) {
    return ACTIVITY_FIELD_MESSAGES.startDate.tooFarAhead;
  }
  // @since NEXT_VERSION
  if (startOfLocalDay(startDate) < earliestDate) {
    return ACTIVITY_FIELD_MESSAGES.startDate.tooFarBack;
  }
  return null;
};

const validateEndDateField = (startDate, endDate) => {
  if (isEmpty(endDate)) return ACTIVITY_FIELD_MESSAGES.endDate.required;
  if (new Date(startDate).getTime() !== new Date(endDate).getTime()) {
    return ACTIVITY_FIELD_MESSAGES.endDate.mismatch;
  }
  return null;
};

const validateStatusField = (status) => {
  if (isEmpty(status)) return ACTIVITY_FIELD_MESSAGES.status.required;
  const ok = AppConstants.ACTIVITY_STATUSES.some(
    (s) => s.value.toUpperCase() === String(status).toUpperCase()
  );
  return ok ? null : ACTIVITY_FIELD_MESSAGES.status.required;
};

const validateDescriptionField = (description) => {
  if (isEmpty(description)) return null;
  return description.length < 255 ? null : ACTIVITY_FIELD_MESSAGES.description.tooLong;
};

/**
 * @param options.earliestDate local midnight of the oldest day the activity may be dated. Defaults
 *   to the standard backdating window. The edit form relaxes it to the date the activity was
 *   already saved with, so a record older than the window stays correctable — the window exists to
 *   stop new work being logged against a closed period, not to freeze what is already in it.
 *   @since NEXT_VERSION
 */
export const validateActivity = (
  activityObject,
  { earliestDate = getEarliestActivityDate() } = {}
) => {
  const fieldErrors = {};

  // Polymorphic owner: when isMember === false, the actor is a manager and
  // ownerId is intentionally null — validate managerId instead.
  if (activityObject.isMember === false) {
    if (isEmpty(activityObject.managerId)) {
      fieldErrors.managerId = ACTIVITY_FIELD_MESSAGES.managerId.required;
    }
  } else {
    const owner = validateOwnerField(activityObject.ownerId);
    if (owner) fieldErrors.ownerId = owner;
  }

  const type = validateActivityTypeField(activityObject.activityTypeId);
  if (type) fieldErrors.activityTypeId = type;

  const start = validateStartDateField(activityObject.startDate, earliestDate);
  if (start) fieldErrors.startDate = start;

  const end = validateEndDateField(activityObject.startDate, activityObject.endDate);
  if (end) fieldErrors.endDate = end;

  const status = validateStatusField(activityObject.status);
  if (status) fieldErrors.status = status;

  const hours = validateHoursField(activityObject.hoursSpent);
  if (hours) fieldErrors.hoursSpent = hours;

  const desc = validateDescriptionField(activityObject.description);
  if (desc) fieldErrors.description = desc;

  return {
    isValid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
  };
};

export const validateActivityObject = (activityObject) =>
  validateActivity(activityObject).isValid;

export const validateActivityTypeObject = (activityTypeObject) => {
  let isValid =
    !isEmpty(activityTypeObject.activityDesc) &&
    activityTypeObject.activityDesc.length < 255;
  isValid = isValid && !isEmpty(activityTypeObject.order);
  isValid = isValid && !isEmpty(activityTypeObject.clientId);
  return isValid;
};

// Same calendar day, whatever mix of Date objects and date-only strings the callers pass.
export const isValidDates = (startDate, endDate) =>
  startOfLocalDay(startDate).getTime() === startOfLocalDay(endDate).getTime();

export const isValidStartDate = (startDate) =>
  !isEmpty(startDate) &&
  startOfLocalDay(startDate) <= getLatestActivityDate() &&
  startOfLocalDay(startDate) >= getEarliestActivityDate();

export const isValidDescription = (description) =>
  isEmpty(description) || description.length < 255;

export const isValidClientTicketRef = () => true;

export const isValidStatus = (status) =>
  !isEmpty(status) &&
  AppConstants.ACTIVITY_STATUSES.some(
    (s) => s.value.toUpperCase() === String(status).toUpperCase()
  );
